import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command, Send } from 'lucide-react';
import api from '@/lib/api';

const REF_RX = /^(PAY|ORD|DLV|REC|DEBT)[-\w]*$/i;
const KIND_COLOR = { PAY: '#047857', ORD: '#1D4ED8', DLV: '#6D28D9', REC: '#9A3412', DEBT: '#B91C1C' };

export function CommandPalette({ open, onClose, sections, t, hasAccess, onAction }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const allItems = useMemo(() => {
    const items = [];
    if (!sections) return items;
    sections.forEach(section => {
      section.items.forEach(item => {
        if (item.permKey !== null && hasAccess && !hasAccess(item.permKey)) return;
        items.push({
          label: t ? t(item.labelKey) : item.labelKey,
          path: item.path,
          icon: item.icon,
          section: 'Navigate',
        });
        if (item.children) {
          item.children.forEach(child => {
            if (child.permKey !== null && hasAccess && !hasAccess(child.permKey)) return;
            items.push({
              label: t ? t(child.labelKey) : child.labelKey,
              path: child.path,
              icon: item.icon,
              section: 'Navigate',
            });
          });
        }
      });
    });
    items.push(
      { label: 'Toggle Theme', section: 'Actions', action: 'theme' },
      { label: 'Collapse Sidebar', section: 'Actions', action: 'collapse' },
    );
    return items;
  }, [sections, t, hasAccess]);

  const [refItems, setRefItems] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (!(q.length >= 4 && REF_RX.test(q))) { setRefItems([]); setRefLoading(false); return; }
    let alive = true;
    setRefLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/refs/lookup?q=${encodeURIComponent(q)}`);
        if (!alive) return;
        setRefItems((r.data.results || []).map(x => ({
          label: x.ref, section: 'References', path: x.target, icon: Send, kind: x.kind,
          sub: [x.title, x.apteka_name, x.amount != null ? `${Number(x.amount).toLocaleString('en-US', { maximumFractionDigits: 2 })} TJS` : null, x.date, x.detail].filter(Boolean).join(' · '),
        })));
      } catch { if (alive) setRefItems([]); }
      if (alive) setRefLoading(false);
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return [...refItems, ...allItems.filter(i => i.label.toLowerCase().includes(q))];
  }, [query, allItems, refItems]);

  useEffect(() => setSelected(0), [filtered]);

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && filtered[selected]) execute(filtered[selected]);
    if (e.key === 'Escape') onClose();
  };

  const execute = (item) => {
    if (item.path) navigate(item.path);
    if (item.action) onAction?.(item.action);
    onClose();
  };

  if (!open) return null;

  const sectionNames = [...new Set(filtered.map(i => i.section))];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[900]" onClick={onClose} data-testid="cmd-palette-backdrop" />
      <div className="cmd-palette fixed top-[18%] left-1/2 -translate-x-1/2 w-[520px] max-w-[90vw] bg-card rounded-2xl border border-border shadow-2xl z-[901] overflow-hidden" data-testid="command-palette">
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <Command className="w-[18px] h-[18px] text-primary shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Type a command, page or Ref (PAY-2026…)"
            className="flex-1 bg-transparent border-none outline-none text-[15px] font-medium text-foreground placeholder:text-muted-foreground"
            data-testid="command-palette-input"
          />
          <kbd className="text-[11px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-mono">ESC</kbd>
        </div>
        <div className="max-h-[340px] overflow-y-auto py-2">
          {sectionNames.map(sec => (
            <div key={sec}>
              <div className="text-[10.5px] font-bold text-muted-foreground/50 uppercase tracking-[0.1em] px-[18px] py-1.5">{sec}</div>
              {filtered.filter(i => i.section === sec).map((item, idx) => {
                const globalIdx = filtered.indexOf(item);
                const isSelected = selected === globalIdx;
                return (
                  <div
                    key={`${sec}-${idx}-${item.path || item.action}`}
                    onClick={() => execute(item)}
                    onMouseEnter={() => setSelected(globalIdx)}
                    className={`flex items-center gap-3 px-[18px] py-2.5 cursor-pointer transition-all duration-100 border-l-2
                      ${isSelected ? 'bg-primary/10 border-primary' : 'border-transparent hover:bg-muted'}`}
                    data-testid={`cmd-item-${(item.path || item.action || '').replace(/\//g, '-')}`}
                  >
                    {item.icon && <item.icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />}
                    <span className="min-w-0 flex-1">
                      <span className={`text-[14px] ${isSelected ? 'font-semibold text-primary' : 'text-foreground'} ${item.kind ? 'font-mono font-bold' : ''}`} data-testid={item.kind ? `cmd-ref-${item.label}` : undefined}>
                        {item.kind && <span className="mr-2 rounded px-1.5 py-0.5 text-[9px] font-black text-white align-middle" style={{ background: KIND_COLOR[item.kind] || '#374151' }}>{item.kind}</span>}
                        {item.label}
                      </span>
                      {item.sub && <span className="block text-[11px] text-muted-foreground truncate">{item.sub}</span>}
                    </span>
                    {isSelected && <span className="ml-auto text-[11px] text-muted-foreground font-mono">↵</span>}
                  </div>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-7 text-sm text-muted-foreground" data-testid="cmd-no-results">{refLoading ? 'Looking up reference…' : <>No results for &ldquo;{query}&rdquo;</>}</div>
          )}
          {!query.trim() && (
            <div className="px-[18px] pt-2 pb-1 text-[10.5px] text-muted-foreground" data-testid="cmd-ref-hint">Tip: paste a Ref from Telegram (PAY-…, ORD-…, DLV-…, REC-…) to jump straight to the record.</div>
          )}
        </div>
        <div className="px-[18px] py-2.5 border-t border-border flex gap-4">
          {[['↑↓', 'Navigate'], ['↵', 'Select'], ['Esc', 'Close']].map(([k, l]) => (
            <div key={k} className="flex items-center gap-1.5">
              <kbd className="text-[11px] px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground font-mono">{k}</kbd>
              <span className="text-[11px] text-muted-foreground">{l}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
